import { type FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { PageType } from '@core/blocks/edu-flow/components/Page/types';
import getNumOfSkippedQuestion from '@core/blocks/edu-flow/components/Page/utils/getNumOfSkippedQuestion';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { type RootState } from '@core/store';

import styles from './ProgressBar.module.css';

type ProgressBarProps = {
  flow: PageType[];
  page: PageType;
};

const ProgressBar: FC<ProgressBarProps> = ({ flow, page }) => {
  const [progress, setProgress] = useState(0);
  const store = useSelector((state: RootState) => state);
  const flags = useFeatureFlags();

  // Figure out what page we are on and use that to determine where we are percent wise
  useEffect(() => {
    // Determine the index of the current page
    const currentPageIndex = flow.findIndex((p) => p.slug === page.slug);

    if (currentPageIndex === -1) return;

    // Calculate the actual step number accounting for skipped questions
    const currentStepNumber = currentPageIndex + 1 - getNumOfSkippedQuestion(store, flow, currentPageIndex, flags);

    // Calculate total effective steps (total pages minus all skipped questions)
    const totalSkippedQuestions = getNumOfSkippedQuestion(store, flow, flow.length, flags);
    const totalEffectiveSteps = flow.length - totalSkippedQuestions;

    // Calculate progress percentage
    // If we're on the first step, default to 10% so we show _something_
    const percent = currentStepNumber > 1 ? (currentStepNumber / totalEffectiveSteps) * 100 : 10;

    setProgress(percent);
  }, [page, store, flags, flow]);

  return (
    <div data-testid="progress-bar" className={styles.progressBar}>
      <div className={styles.progress} style={{ width: `${progress}%` }} />
    </div>
  );
};
export default ProgressBar;
